import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf, Sparkles, Wheat } from "lucide-react";
import { scrollToSection } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1788692944110-130f3ef845ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxtaWxsZXQlMjBncmFpbiUyMG9yZ2FuaWMlMjBmb29kJTIwYm93bHxlbnwwfHx8fDE3OTEwMTg4MzR8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Pure Heritage.", "Sprouted Nutrition.", "Crafted with Love."];

const EASE = [0.22, 1, 0.36, 1];
const PARTICLES = ["grain-a", "grain-b", "grain-c", "grain-d", "grain-e", "grain-f", "grain-g"].map((id, i) => ({
  id, style: { left: `${8 + i * 13}%`, top: `${18 + (i % 3) * 24}%` },
  transition: { duration: 5 + i, repeat: Infinity, delay: i * 0.6, ease: "easeInOut" },
}));
const PARTICLE_MOTION = { y: [0, -18, 0], opacity: [0.3, 0.9, 0.3] };

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="hero" ref={ref} className="grain relative overflow-hidden bg-cream pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-saffron/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-[420px] w-[420px] rounded-full bg-terracotta/10 blur-3xl" />

      {PARTICLES.map((particle) => (
        <motion.span
          key={particle.id}
          className="pointer-events-none absolute h-1.5 w-1.5 rounded-full bg-saffron/60"
          style={particle.style}
          animate={PARTICLE_MOTION}
          transition={particle.transition}
        />
      ))}

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-terracotta"
            data-testid="hero-eyebrow"
          >
            <Wheat className="h-4 w-4" /> Kandula Home Foods · Made with Love by Kandulas
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight text-ragi-dark" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line} className="mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.16, ease: EASE }}
                >
                  {i === 2 ? (
                    <>
                      Crafted with <em className="text-terracotta not-italic font-semibold italic">Love</em>.
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-muted-foreground"
            data-testid="hero-subtext"
          >
            From ancestral sprouted millet protein laddus to hand-poured 3D chocolates
            and rich Tirunelveli wheat halwa — 100% natural, preservative-free home
            foods, made in micro-batches and brought to your doorstep.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("catalog")}
              data-testid="hero-shop-button"
              className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-orange-900/20 transition-all duration-300 hover:scale-[1.04] hover:bg-terracotta-dark"
            >
              Explore Heritage Shop
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToSection("enquiry")}
              data-testid="hero-enquiry-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-ragi px-8 py-[14px] text-sm font-semibold text-ragi transition-all duration-300 hover:bg-ragi hover:text-white"
            >
              Request Bulk Enquiry
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-6"
            data-testid="hero-stats"
          >
            {[
              ["48 hrs", "slow sprouting"],
              ["0", "preservatives"],
              ["30+", "handcrafted items"],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-display text-2xl font-bold text-ragi-dark">{num}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] border-[6px] border-cream-secondary shadow-2xl shadow-ragi/20">
            <motion.img
              src={HERO_IMG}
              alt="Sprouted millet grains in a rustic bowl"
              style={{ y: imgY }}
              className="h-[420px] sm:h-[540px] w-full scale-110 object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ragi-dark/35 via-transparent to-transparent" />
          </div>

          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-4 sm:-left-8 top-24 flex items-center gap-2 rounded-2xl border border-cream-muted bg-cream/95 px-4 py-3 shadow-xl backdrop-blur"
            data-testid="hero-badge-sprouted"
          >
            <Leaf className="h-5 w-5 text-ragi" />
            <div>
              <p className="text-xs font-bold text-ragi-dark">48h Sprouted</p>
              <p className="text-[10px] text-muted-foreground">7 heirloom millets</p>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -right-3 sm:-right-6 bottom-16 flex items-center gap-2 rounded-2xl border border-cream-muted bg-cream/95 px-4 py-3 shadow-xl backdrop-blur"
            data-testid="hero-badge-natural"
          >
            <Sparkles className="h-5 w-5 text-saffron" />
            <div>
              <p className="text-xs font-bold text-ragi-dark">Zero Preservatives</p>
              <p className="text-[10px] text-muted-foreground">made after you order</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
